import { css, nothing, html } from "lit";
import { query, state } from "lit/decorators.js";
import { ifDefined } from "lit-html/directives/if-defined.js";
import "../../atom/Icon/index.js";
import "../../atom/ClickOutside/index.js";
import "../Button/index.js";
import "../Link/index.js";
import "../Toggle/index.js";
import "../Dropdown/index.js";
import "../DateInput/index.js";
import "../Calendar/index.js";
import "../FilterButton/index.js";
import { BaseComponent } from "../../BaseComponent.js";
import { defineElement } from "../../../decorators/defineElement.js";
import { prop } from "../../../decorators/prop/index.js";
import { normalizeLocale, rangeHasChanged, formatLocaleDateRange } from "../dates.js";
import { preventDefault } from "../../../decorators/preventDefault.js";
import { dateRangeAttributeConverter, dateAttributeConverter, dateListAttributeConverter } from "../attribute-converters.js";
import { dateOrStringHasChanged, dateArrayHasChanged } from "../change-detection.js";
import { classNames } from "../../../styles/index.js";
import { isMobile } from "../../../responsive/index.js";
import { testingAttr } from "../../../directives/testingAttr.js";
import { TestingAttrs } from "./datePicker.selectors.js";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
let DsfDatePicker = class extends BaseComponent {
  constructor() {
    super();
    this.value = [];
    this.disabled = false;
    this.dsf_open_on_focus = false;
    this.dsf_mobile = "auto";
    this.dsf_trigger = "input";
    this.dsf_icon = "Calendar";
    this.dsf_mode = "single";
    this.dsf_locale = navigator.language;
    this.dsf_visible_months = 2;
    this.dsf_disabled_dates = [];
    this.dsf_highlight_today = true;
    this.dsf_highlight_weekends = false;
    this.dsf_highlighted_dates = [];
    this.dsf_overnight = false;
    this.dsf_error = false;
    this.dsf_success = false;
    this.dsf_badge_square = false;
    this.dsf_size = "medium";
    this.dsf_open = false;
    this.dsf_placement = "bottom-start";
    this.dsf_flip = true;
    this.dsf_shift = true;
    this.dsf_hide = false;
    this._hasDefaultSlotContent = false;
    this._hasTimespanSlotContent = false;
    this._hasLeftSlotContent = false;
    this._calendarValue = [];
    this._shouldOpenOnFocus = false;
    this._upperMostParent = null;
    this.handleKeydown = this.handleKeydown.bind(this);
    this.addEventListener("keydown", this.handleKeydown);
  }
  get calendarMonths() {
    if (this.isMobileView) {
      return 4;
    }
    return this.dsf_visible_months < 1 ? 1 : this.dsf_visible_months > 2 ? 2 : this.dsf_visible_months;
  }
  get isMobileView() {
    if (this.dsf_mobile === "on") {
      return true;
    }
    if (this.dsf_mobile === "off") {
      return false;
    }
    return isMobile();
  }
  get localeNormalized() {
    return normalizeLocale(this.dsf_locale);
  }
  focus() {
    var _a;
    (_a = this.dateInput) == null ? void 0 : _a.focus();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.restoreGlobalStyles();
  }
  update(changedProps) {
    if (changedProps.has("value")) {
      this._calendarValue = this.value;
    }
    if (changedProps.has("dsf_open")) {
      if (this.dsf_open) {
        this.resetCalendar();
      }
      if (this.isMobileView) {
        this.adjustMobileModal();
      }
    }
    if (changedProps.has("dsf_open_on_focus")) {
      this._shouldOpenOnFocus = this.dsf_open_on_focus;
    }
    super.update(changedProps);
  }
  resetCalendar() {
    var _a;
    this._calendarValue = this.value;
    (_a = this.calendar) == null ? void 0 : _a.refresh();
  }
  handleKeydown(ev) {
    const key = ev.key;
    if (!this.dsf_open && ["ArrowUp", "ArrowDown", "Enter", " "].includes(key)) {
      this.toggleDropdown(true);
      ev.preventDefault();
    } else if (this.dsf_open) {
      if (key === "Escape") {
        this.closeAndFocus();
      }
    }
  }
  handleClickOutside() {
    if (this.dsf_open) {
      this.toggleDropdown(false);
    }
  }
  handleDateInputMousedown(ev) {
    if (this.disabled) {
      return;
    }
    this.toggleDropdown(true);
    if (this.isMobileView) {
      ev.preventDefault();
    }
  }
  handleButtonMousedown() {
    if (this.disabled) {
      return;
    }
    this.toggleDropdown(!this.dsf_open);
  }
  handleInputFocusIn() {
    if (this._shouldOpenOnFocus) {
      this.toggleDropdown(true);
    }
    this._shouldOpenOnFocus = this.dsf_open_on_focus;
  }
  closeAndFocus() {
    this.toggleDropdown(false);
    this._shouldOpenOnFocus = false;
    this.focus();
  }
  toggleDropdown(open) {
    const oldOpen = this.dsf_open;
    this.dsf_open = open;
    if (oldOpen !== this.dsf_open) {
      this.emit({ name: "open-changed", payload: this.dsf_open });
    }
  }
  adjustMobileModal() {
    var _a, _b;
    const mobileContent = (_a = this.shadowRoot) == null ? void 0 : _a.querySelector("#mobileContent");
    if (this.dsf_open) {
      let upperMostParent = this.parentElement;
      while (upperMostParent && upperMostParent.parentElement && upperMostParent.parentElement.tagName !== "BODY") {
        upperMostParent = upperMostParent.parentElement;
      }
      this._upperMostParent = upperMostParent;
      this._contentHeight = mobileContent == null ? void 0 : mobileContent.style.height;
      document.body.classList.add(classNames.disableScroll);
      if (mobileContent) {
        const viewportHeight = (_b = window.visualViewport) == null ? void 0 : _b.height;
        if (viewportHeight) {
          mobileContent.style.height = viewportHeight + "px";
        }
      }
      if (upperMostParent) {
        upperMostParent.classList.add(classNames.disableScroll);
      }
    } else {
      if (mobileContent) {
        mobileContent.style.height = this._contentHeight || "";
      }
      this.restoreGlobalStyles();
    }
  }
  restoreGlobalStyles() {
    document.body.classList.remove(classNames.disableScroll);
    if (this._upperMostParent) {
      this._upperMostParent.classList.remove(classNames.disableScroll);
      this._upperMostParent = null;
    }
  }
  setValue(value) {
    const oldValue = this.value;
    this.value = value;
    this._calendarValue = value;
    if (rangeHasChanged(oldValue, this.value)) {
      this.emit({ name: "input", payload: this.value });
    }
  }
  handleDateInput({ detail: newValue }) {
    this.setValue(newValue);
  }
  handleCalendarDateInput({ detail }) {
    this._calendarValue = detail;
    const applyOnSelection = !this.dsf_apply_button_text;
    if (applyOnSelection) {
      this.setValue(this._calendarValue);
    }
  }
  handleMultiDayToggleChange({ detail: checked }) {
    this.dsf_mode = checked ? "range" : "single";
    this.setValue([]);
    this.emit({ name: "mode-changed", payload: this.dsf_mode });
  }
  handleMultiDayToggleInput() {
  }
  handleDefaultSlotChange() {
    this._hasDefaultSlotContent = !!this.defaultSlotElement && this.defaultSlotElement.assignedElements({ flatten: true }).length > 0;
  }
  handleTimespanSlotChange() {
    this._hasTimespanSlotContent = !!this.timespanSlotElement && this.timespanSlotElement.assignedElements({ flatten: true }).length > 0;
  }
  handleLeftSlotChange() {
    this._hasLeftSlotContent = !!this.leftSlotElement && this.leftSlotElement.assignedElements({ flatten: true }).length > 0;
  }
  handleClearClick() {
    this.setValue([]);
    this.emit("clear");
  }
  handleCancelClick() {
    this.resetCalendar();
    this.emit("cancel");
    this.toggleDropdown(false);
  }
  handleApplyClick() {
    this.setValue(this._calendarValue);
    this.emit("apply");
    this.toggleDropdown(false);
  }
  handleClearIconClick() {
    this.handleClearClick();
  }
  handleCloseButtonClick() {
    this.toggleDropdown(false);
  }
  suppressEvent() {
  }
  renderHeader() {
    const showHeader = this.dsf_title_text || this.dsf_mode_toggle_text || this.isMobileView;
    if (!showHeader) {
      return nothing;
    }
    return html`<div
      class=${`flex shrink-0 items-center ${this.isMobileView ? "px-24 pt-16" : "px-16 pt-16"}`}
    >
      ${this.dsf_title_text ? html` <div class="flex items-center gap-8">
            <dsf-icon dsf_name=${ifDefined(this.dsf_icon)} class="h-16 w-16"></dsf-icon>
            <span class="dsf-text-s-semibold shrink-0">${this.dsf_title_text}</span>
          </div>` : nothing}
      <div class="ml-auto flex items-center gap-16">
        ${this.dsf_mode_toggle_text ? html`<dsf-toggle
              .dsf_checked=${this.dsf_mode === "range"}
              dsf_size="small"
              dsf_label=${this.dsf_mode_toggle_text}
              data-mode-toggle
              @change=${this.handleMultiDayToggleChange}
              @input=${this.handleMultiDayToggleInput}
            ></dsf-toggle>` : nothing}
        ${this.isMobileView ? html`<dsf-icon-button
              dsf_icon="X"
              @click=${this.handleCloseButtonClick}
              dsf_title=${ifDefined(this.dsf_close_button_text)}
            ></dsf-icon-button>` : nothing}
      </div>
    </div>`;
  }
  renderFooter() {
    const showFooter = this.dsf_clear_button_text || this.dsf_apply_button_text || this.dsf_cancel_button_text;
    if (!showFooter) {
      return nothing;
    }
    return html`<div
      class=${`flex shrink-0 items-center border-t border-t-neutral-100 ${this.isMobileView ? "p-16" : "p-12"}`}
    >
      ${this.dsf_clear_button_text ? html`<dsf-link
            data-clear
            @click=${this.handleClearClick}
            ${testingAttr(TestingAttrs.clearButton)}
            >${this.dsf_clear_button_text}</dsf-link
          >` : nothing}
      <div class="ml-auto flex items-center gap-8">
        ${this.dsf_cancel_button_text ? html`<dsf-button
              dsf_size="small"
              dsf_level="secondary"
              data-cancel
              @click=${this.handleCancelClick}
              ${testingAttr(TestingAttrs.cancelButton)}
            >
              ${this.dsf_cancel_button_text}
            </dsf-button>` : nothing}
        ${this.dsf_apply_button_text ? html`<dsf-button
              dsf_size="small"
              data-apply
              @click=${this.handleApplyClick}
              ${testingAttr(TestingAttrs.applyButton)}
              >${this.dsf_apply_button_text}
            </dsf-button>` : nothing}
      </div>
    </div>`;
  }
  renderCalendar() {
    return html` <dsf-calendar
      id="calendar"
      .value=${this._calendarValue}
      ?dsf_mobile=${this.isMobileView}
      .dsf_visible_months=${this.calendarMonths}
      .dsf_mode=${this.dsf_mode}
      .dsf_locale=${this.localeNormalized}
      .dsf_min_date=${this.dsf_min_date}
      .dsf_max_date=${this.dsf_max_date}
      .dsf_disabled_dates=${this.dsf_disabled_dates}
      .dsf_highlight_today=${this.dsf_highlight_today}
      .dsf_highlight_weekends=${this.dsf_highlight_weekends}
      .dsf_highlighted_dates=${this.dsf_highlighted_dates}
      .dsf_prev_button_title=${this.dsf_prev_button_title}
      .dsf_next_button_title=${this.dsf_next_button_title}
      .dsf_overnight=${this.dsf_overnight}
      @input=${this.handleCalendarDateInput}
    >
    </dsf-calendar>`;
  }
  renderDesktopContent() {
    const header = this.renderHeader();
    const footer = this.renderFooter();
    const calendar = this.renderCalendar();
    return html` <div class="dsf-text-s flex flex-auto flex-col text-neutral-700">
      <div class="flex flex-auto">
        <div
          class=${`flex flex-auto border-r border-r-neutral-100 ${!this._hasLeftSlotContent && "hidden"}`}
        >
          <slot
            name="left"
            @slotchange=${this.handleLeftSlotChange}
            @input=${this.suppressEvent}
          ></slot>
        </div>
        <div class="flex flex-auto flex-col">
          ${header}
          <div class="flex-auto px-16 pb-16 pt-8">${calendar}</div>
          <div
            class=${`flex border-t border-t-neutral-100 ${!this._hasDefaultSlotContent && "hidden"}`}
          >
            <slot @slotchange=${this.handleDefaultSlotChange} @input=${this.suppressEvent}></slot>
          </div>
        </div>
      </div>
      ${footer}
    </div>`;
  }
  renderWeekdays() {
    const weekdays = [];
    const formatter = new Intl.DateTimeFormat(this.localeNormalized, { weekday: "short" });
    for (let i = 2; i <= 8; i++) {
      const date = new Date(2023, 0, i);
      const weekday = formatter.format(date);
      weekdays.push(
        html`<span
          class="dsf-text-xxs-semibold flex flex-auto items-center justify-center uppercase text-neutral-300"
          >${weekday}</span
        >`
      );
    }
    return html`<div
      class="flex items-center border-b border-b-neutral-100 pb-24 pl-16 pr-28 pt-12"
    >
      ${weekdays}
    </div>`;
  }
  renderMobileContent() {
    const header = this.renderHeader();
    const footer = this.renderFooter();
    const calendar = this.renderCalendar();
    const weekdays = this.renderWeekdays();
    return this.dsf_open ? html` <div
          id="mobileContent"
          class="mobile-content dsf-text-s fixed left-0 top-0 z-dropdown flex w-screen flex-auto flex-col overflow-hidden bg-neutral-0 text-neutral-700"
        >
          ${header} ${weekdays}
          <div class="flex-auto overflow-y-auto p-16">${calendar}</div>
          <div
            class=${`flex flex-auto border-t border-t-neutral-100 ${!this._hasLeftSlotContent && "hidden"}`}
          >
            <slot name="left" @slotchange=${this.handleLeftSlotChange}></slot>
          </div>
          <div
            class=${`flex flex-auto border-t border-t-neutral-100 ${!this._hasDefaultSlotContent && "hidden"}`}
          >
            <slot @slotchange=${this.handleDefaultSlotChange}></slot>
          </div>
          ${footer}
        </div>` : nothing;
  }
  renderTrigger() {
    return this.dsf_trigger === "input" ? this.renderDateInput() : this.renderDateButton();
  }
  renderDateButton() {
    const displayValue = this.value.length ? this.dsf_display_value || formatLocaleDateRange(this.value, this.localeNormalized) : "";
    return html`<dsf-filter-button
      exportparts="filter-button"
      class="w-full"
      .disabled=${this.disabled}
      dsf_options=${displayValue}
      .dsf_open=${this.dsf_open}
      dsf_placeholder=${ifDefined(this.dsf_placeholder)}
      dsf_icon=${ifDefined(this.dsf_icon)}
      .dsf_size=${this.dsf_size}
      @mousedown=${this.handleButtonMousedown}
      @focusin=${this.handleInputFocusIn}
      @clear-click=${this.handleClearIconClick}
      ${testingAttr(TestingAttrs.filterButton)}
    >
      <slot
        class=${`flex ${this._hasTimespanSlotContent ? "ml-8" : ""}`}
        name="timespan"
        @slotchange=${this.handleTimespanSlotChange}
      ></slot>
    </dsf-filter-button>`;
  }
  renderDateInput() {
    return html`<dsf-date-input
      id="dateInput"
      class="w-full"
      .value=${this.value}
      .disabled=${this.disabled}
      .dsf_size=${this.dsf_size}
      .dsf_mode=${this.dsf_mode}
      .dsf_locale=${this.localeNormalized}
      .dsf_min_date=${this.dsf_min_date}
      .dsf_max_date=${this.dsf_max_date}
      .dsf_error=${this.dsf_error}
      .dsf_success=${this.dsf_success}
      .autofocus=${this.autofocus}
      .dsf_name=${this.dsf_name}
      .dsf_message=${this.dsf_message}
      .dsf_badge=${this.dsf_badge}
      .dsf_badge_square=${this.dsf_badge_square}
      .dsf_icon=${this.dsf_icon}
      @input=${this.handleDateInput}
      @mousedown=${this.handleDateInputMousedown}
      @focusin=${this.handleInputFocusIn}
      ${testingAttr(TestingAttrs.dateInput)}
    >
      <slot name="timespan" slot="timespan"></slot>
    </dsf-date-input>`;
  }
  renderLabel() {
    const classes = [
      "text-neutral-700",
      this.dsf_size === "small" ? "dsf-text-xxs" : "dsf-text-xs",
      this.dsf_size === "small" ? "mb-2" : "mb-4"
    ];
    return this.dsf_label ? html`<span id="label" class=${classes.join(" ")} data-skeleton>${this.dsf_label}</span>` : nothing;
  }
  renderDesktopView() {
    const label = this.renderLabel();
    const trigger = this.renderTrigger();
    const pickerContent = this.renderDesktopContent();
    return html`
      <dsf-click-outside @click-outside=${this.handleClickOutside} class="flex flex-auto flex-col">
        ${label}
        <dsf-dropdown
          class="flex flex-auto flex-col"
          ?dsf_flip=${this.dsf_flip}
          ?dsf_shift=${this.dsf_shift}
          ?dsf_hide=${this.dsf_hide}
          .dsf_open=${this.dsf_open}
          .dsf_placement=${this.dsf_placement}
          .dsf_same_width=${false}
          .dsf_offset=${4}
          data-skeleton
        >
          <div
            slot="anchor"
            role="combobox"
            aria-labelledby="label"
            aria-disabled=${this.disabled ? "true" : "false"}
            aria-expanded=${this.dsf_open ? "true" : "false"}
            aria-controls="content"
          >
            ${trigger}
          </div>
          <div
            id="content"
            class="flex flex-auto flex-col overflow-visible rounded-sm border border-neutral-100 bg-neutral-0 shadow-xl"
            part="dropdown"
            role="dialog"
            aria-labelledby="label"
            aria-expanded=${this.dsf_open ? "true" : "false"}
          >
            ${pickerContent}
          </div>
        </dsf-dropdown>
      </dsf-click-outside>
    `;
  }
  renderMobileView() {
    const label = this.renderLabel();
    const trigger = this.renderTrigger();
    const pickerContent = this.renderMobileContent();
    return html`<div class="flex flex-auto flex-col gap-4">
      ${label} ${trigger} ${pickerContent}
    </div>`;
  }
  render() {
    return this.isMobileView ? this.renderMobileView() : this.renderDesktopView();
  }
};
DsfDatePicker.styles = [
  ...BaseComponent.styles,
  css`
      :host {
        display: inline-flex;
      }

      dsf-dropdown::part(dropdown) {
        z-index: var(--dsf-z-index-dropdown);
      }

      .mobile-content {
        height: 100vh;
        height: -webkit-fill-available;
        height: fill-available;
      }
    `
];
__decorateClass([
  prop({
    type: String,
    reflect: true,
    converter: dateRangeAttributeConverter,
    hasChanged: rangeHasChanged
  })
], DsfDatePicker.prototype, "value", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDatePicker.prototype, "disabled", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDatePicker.prototype, "dsf_open_on_focus", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_title_text", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_clear_button_text", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_apply_button_text", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_cancel_button_text", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_mode_toggle_text", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfDatePicker.prototype, "dsf_mobile", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_close_button_text", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfDatePicker.prototype, "dsf_trigger", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_placeholder", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfDatePicker.prototype, "dsf_icon", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_display_value", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfDatePicker.prototype, "dsf_mode", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_locale", 2);
__decorateClass([
  prop({ type: Number, validator: (val) => val >= 1 && val <= 2 })
], DsfDatePicker.prototype, "dsf_visible_months", 2);
__decorateClass([
  prop({
    type: String,
    converter: dateAttributeConverter,
    hasChanged: dateOrStringHasChanged
  })
], DsfDatePicker.prototype, "dsf_min_date", 2);
__decorateClass([
  prop({
    type: String,
    converter: dateAttributeConverter,
    hasChanged: dateOrStringHasChanged
  })
], DsfDatePicker.prototype, "dsf_max_date", 2);
__decorateClass([
  prop({
    type: Array,
    converter: dateListAttributeConverter,
    hasChanged: dateArrayHasChanged
  })
], DsfDatePicker.prototype, "dsf_disabled_dates", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDatePicker.prototype, "dsf_highlight_today", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDatePicker.prototype, "dsf_highlight_weekends", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_prev_button_title", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_next_button_title", 2);
__decorateClass([
  prop({
    type: Array,
    converter: dateListAttributeConverter,
    hasChanged: dateArrayHasChanged
  })
], DsfDatePicker.prototype, "dsf_highlighted_dates", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDatePicker.prototype, "dsf_overnight", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDatePicker.prototype, "dsf_error", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDatePicker.prototype, "dsf_success", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_name", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_label", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_message", 2);
__decorateClass([
  prop({ type: String })
], DsfDatePicker.prototype, "dsf_badge", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDatePicker.prototype, "dsf_badge_square", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfDatePicker.prototype, "dsf_size", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDatePicker.prototype, "dsf_open", 2);
__decorateClass([
  prop({ type: String, reflect: true })
], DsfDatePicker.prototype, "dsf_placement", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDatePicker.prototype, "dsf_flip", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDatePicker.prototype, "dsf_shift", 2);
__decorateClass([
  prop({ type: Boolean, reflect: true })
], DsfDatePicker.prototype, "dsf_hide", 2);
__decorateClass([
  query("#dateInput")
], DsfDatePicker.prototype, "dateInput", 2);
__decorateClass([
  query("#calendar")
], DsfDatePicker.prototype, "calendar", 2);
__decorateClass([
  query("slot:not([name])")
], DsfDatePicker.prototype, "defaultSlotElement", 2);
__decorateClass([
  query('slot[name="timespan"]')
], DsfDatePicker.prototype, "timespanSlotElement", 2);
__decorateClass([
  query('slot[name="left"]')
], DsfDatePicker.prototype, "leftSlotElement", 2);
__decorateClass([
  state()
], DsfDatePicker.prototype, "_hasDefaultSlotContent", 2);
__decorateClass([
  state()
], DsfDatePicker.prototype, "_hasTimespanSlotContent", 2);
__decorateClass([
  state()
], DsfDatePicker.prototype, "_hasLeftSlotContent", 2);
__decorateClass([
  state()
], DsfDatePicker.prototype, "_calendarValue", 2);
__decorateClass([
  preventDefault
], DsfDatePicker.prototype, "handleDateInput", 1);
__decorateClass([
  preventDefault
], DsfDatePicker.prototype, "handleCalendarDateInput", 1);
__decorateClass([
  preventDefault
], DsfDatePicker.prototype, "handleMultiDayToggleChange", 1);
__decorateClass([
  preventDefault
], DsfDatePicker.prototype, "handleMultiDayToggleInput", 1);
__decorateClass([
  preventDefault
], DsfDatePicker.prototype, "handleClearIconClick", 1);
__decorateClass([
  preventDefault
], DsfDatePicker.prototype, "suppressEvent", 1);
DsfDatePicker = __decorateClass([
  defineElement("dsf-date-picker")
], DsfDatePicker);
export {
  DsfDatePicker
};
